if (!customElements.get('collab-list')) {
  customElements.define(
    'collab-list',
    class CollabList extends HTMLElement {
      connectedCallback() {
        this.speed = parseInt(this.dataset.cycleSpeed, 10) || 450;
        // One shared fixed stage for the whole list; the hovered row's
        // template images are swapped into the stage. Mystery rows have no
        // <template> and never activate.
        this.stage = this.querySelector('[data-preview]');
        this.stageBadge = this.querySelector('[data-stage-badge]');
        this.rows = this.querySelectorAll('[data-collab-row]:not([data-mystery])');
        this.notifyButtons = this.querySelectorAll('[data-notify]');

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.hoverCapable = window.matchMedia('(hover: hover)').matches;

        this.interval = null;
        this.activeRow = null;
        this.observer = null;
        this.imageCache = new Map();

        this._enter = (e) => this.startRow(e.currentTarget);
        this._leave = (e) => this.stopRow(e.currentTarget);
        this._notifyClick = (e) => this.toggleNotify(e.currentTarget);

        if (this.hoverCapable) {
          this.rows.forEach((row) => {
            row.addEventListener('mouseenter', this._enter);
            row.addEventListener('focusin', this._enter);
            row.addEventListener('mouseleave', this._leave);
            row.addEventListener('focusout', this._leave);
          });
        }
        // Touch devices need no JS here: archive rows carry a server-rendered
        // inline thumb (.collab-row__thumb, shown by CSS under 750px).

        this.notifyButtons.forEach((button) => {
          button.addEventListener('click', this._notifyClick);
        });

        // "En cours" multi-product collabs slide product-by-product: every
        // slot in a section shows the SAME product's images at once, with the
        // captions and card links swapping in sync. Slide count is the MAX
        // across slots — manual override slots are static (one image) and
        // stay put (modulo) while the dynamic slots cycle. Single-product
        // sections (one image per slot) stay static.
        this.currentSections = [];
        this.querySelectorAll('.collab-current').forEach((sectionEl) => {
          const cards = Array.from(sectionEl.querySelectorAll('[data-current-card]'));
          if (!cards.length) return;
          const slides = Math.max(
            ...cards.map((card) => card.querySelectorAll('.collab-current__img').length)
          );
          // Captions (name/price) exist only under the first slot.
          const captions = Array.from(cards[0].querySelectorAll('.collab-current__caption'));
          if (slides > 1) this.currentSections.push({ cards, captions, slides, i: 0 });
        });
        this.currentInterval = null;
        if (this.currentSections.length && !this.reducedMotion) {
          this.currentInterval = setInterval(() => {
            this.currentSections.forEach((s) => {
              s.i = (s.i + 1) % s.slides;
              s.cards.forEach((card) => {
                const imgs = card.querySelectorAll('.collab-current__img');
                imgs.forEach((img, k) => {
                  img.classList.toggle('active', k === s.i % imgs.length);
                });
              });
              if (!s.captions.length) return;
              const captionIndex = s.i % s.captions.length;
              s.captions.forEach((caption, k) => {
                caption.hidden = k !== captionIndex;
              });
              const active = s.captions[captionIndex];
              if (active && active.dataset.url) {
                s.cards.forEach((card) => card.setAttribute('href', active.dataset.url));
              }
            });
          }, Math.max(this.speed * 4, 2000));
        }
      }

      disconnectedCallback() {
        this.clearTimer();
        if (this.currentInterval) {
          clearInterval(this.currentInterval);
          this.currentInterval = null;
        }
        if (this.hoverCapable) {
          this.rows?.forEach((row) => {
            row.removeEventListener('mouseenter', this._enter);
            row.removeEventListener('focusin', this._enter);
            row.removeEventListener('mouseleave', this._leave);
            row.removeEventListener('focusout', this._leave);
          });
        }
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.notifyButtons?.forEach((button) => {
          button.removeEventListener('click', this._notifyClick);
        });
      }

      clearTimer() {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
      }

      toggleNotify(button) {
        const li = button.closest('li');
        const form = li ? li.querySelector('.collab-row__notify-form') : null;
        if (!form) return;
        const isHidden = form.hasAttribute('hidden');
        if (isHidden) {
          form.removeAttribute('hidden');
        } else {
          form.setAttribute('hidden', '');
        }
        button.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      }

      // Swap the row's images into the shared stage and cycle if multi-image.
      startRow(row) {
        if (!row || this.activeRow === row) return;
        if (this.activeRow) this.stopRow(this.activeRow);
        this.activeRow = row;

        if (!this.stage) return;
        let images = this.imageCache.get(row);
        if (!images) {
          const template = row.querySelector('.collab-row__template');
          if (!template) return; // degenerate data: non-mystery row with no template
          const fragment = template.content.cloneNode(true);
          images = Array.from(fragment.querySelectorAll('.collab-row__img'));
          this.imageCache.set(row, images);
        }
        if (this.stageBadge) {
          const badge = row.dataset.badge || '';
          this.stageBadge.textContent = badge;
          this.stageBadge.hidden = !badge;
          this.stageBadge.className =
            'collab-list__stage-badge' +
            (row.dataset.badgeType ? ' collab-list__stage-badge--' + row.dataset.badgeType : '');
          this.stage.replaceChildren(...images, this.stageBadge);
        } else {
          this.stage.replaceChildren(...images);
        }
        if (!images.length) return;

        images.forEach((img) => img.classList.remove('active'));
        images[0].classList.add('active');
        this.clearTimer();
        if (images.length > 1 && !this.reducedMotion) {
          let i = 0;
          this.interval = setInterval(() => {
            images[i].classList.remove('active');
            i = (i + 1) % images.length;
            images[i].classList.add('active');
          }, this.speed);
        }
      }

      // Stop cycling and clear the stage — the image only shows while its
      // row is hovered/centered.
      stopRow(row) {
        if (!row) return;
        // The interval is shared; only the row that owns it may clear it, or a
        // stale IntersectionObserver "leave" entry would kill a newer row's cycle
        // (and wipe the stage another row just populated).
        if (this.activeRow === row) {
          this.clearTimer();
          this.activeRow = null;
          if (this.stage) {
            if (this.stageBadge) {
              this.stageBadge.hidden = true;
              this.stage.replaceChildren(this.stageBadge);
            } else {
              this.stage.replaceChildren();
            }
          }
        }
      }
    }
  );
}
